import React, { memo } from 'react';
import PropTypes from 'prop-types';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Checkbox,
} from '@mui/material';

import { styled } from '@mui/material/styles';

import DateCell from './DateCell';
import StepCell from './StepCell';
import FileCell from './FileCell';
import IndicatorCell from './IndicatorCell';
import CaseActionCell from './CaseActionCell';
import HeaderCell from './HeaderCell';
import TablePagination from './TablePagination';

import { utils } from '../../../util';
import { caseConstants } from '../../../constants';

const {
  fileTypes,
  statuses,
  numberOfSteps,
  numberOfAClSteps,
  numberOfCmfSteps,
  numberOfCmfExtendedSteps,
  procedureFlows,
  cmfExtendedProcedureFlows,
} = caseConstants;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
  verticalAlign: 'middle',
}));

const StyledHeaderCheckboxCell = styled(TableCell)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  background: theme.palette.mode === 'dark' ? 'var(--color-surface-elevated)' : '#fafafa',
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '12px 16px',
  width: 56,
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:hover': {
    background: 'var(--color-main-light) !important',
  },
}));

const checkboxSx = {
  p: 0.5,
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
};

const getNumberOfSteps = (procedureFlow) => {
  if (procedureFlow === procedureFlows.ACL) {
    return numberOfAClSteps;
  }
  if (procedureFlow === procedureFlows.CMF) {
    return numberOfCmfSteps;
  }
  if (cmfExtendedProcedureFlows.includes(procedureFlow)) {
    return numberOfCmfExtendedSteps;
  }
  return numberOfSteps;
};

const renderCell = (column, row) => {
  const procedureFlow = row && row.procedureFlow;
  const numberFlowSteps = getNumberOfSteps(procedureFlow);
  const type = column.type;
  const value = row[column.field];
  const complete = row && row.step > numberFlowSteps;
  const rejected = row && row.status === statuses.rejected;
  const access = row && row.access;
  const createdAt = row?.createdAt ? row?.createdAt : false;
  const getFlowTheme = utils.getFlowsTheme(row?.procedureFlow, row.procedure).main;

  switch (type) {
    case 'default':
      return column.formatter ? column.formatter(value) : value;

    case 'date':
      return <DateCell date={value} />;

    case 'step': {
      const step = typeof value === 'object' ? value.currentStep : value;
      const max = typeof value === 'object' ? value.max : numberFlowSteps;
      return <StepCell step={step} max={max} style={getFlowTheme} />;
    }

    case 'file': {
      let files = [];

      if (row?.files) {
        if (column.field === 'scan') {
          files = row.files.filter(
            (item) => item.type === fileTypes.scan || item.type === fileTypes.xRay
          );
        } else {
          files = row.files.filter((item) => item.type === column.field);
        }
      }

      return !!files.length && <FileCell onClick={() => column.onClick(row)} icon={column.icon} />;
    }

    case 'indicator':
      return (
        <IndicatorCell
          expiry={createdAt}
          value={value}
          complete={complete}
          rejected={rejected}
          style={getFlowTheme}
          archived={!!row.archived}
          postponed={!!row.postponed}
        />
      );

    case 'caseAction': {
      const numberSteps = typeof value === 'object' ? value.max : numberFlowSteps;
      return (
        <CaseActionCell
          step={row.step}
          flow={procedureFlow}
          numberSteps={numberSteps}
          access={access}
          style={getFlowTheme}
        />
      );
    }

    case 'custom':
      return column.formatter(value, row);

    default:
      return value;
  }
};

const TableComponent = (props) => {
  const {
    columns = [],
    rows = [],
    onRowClick = () => {},
    onFilterChange = () => {},
    currentFilter = {},
    filterOptions = {},
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    withoutPagination,
    loading,
    selectableRows = false,
    selectedRowIds = [],
    onToggleRow,
    onToggleAllRows,
    rowIdField = 'id',
  } = props;

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const totalPages = Math.max(
    1,
    Math.ceil(totalRows / rowsPerPage)
  );

  const visibleRowIds = rows
    .map((row) => row?.[rowIdField])
    .filter(Boolean);

  const allVisibleRowsSelected = !!visibleRowIds.length
    && visibleRowIds.every((id) => selectedRowIds.includes(id));

  const someVisibleRowsSelected = visibleRowIds.some((id) => selectedRowIds.includes(id));

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(15,23,32,0.68)' : 'rgba(255,255,255,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <CircularProgress size={36} />
        </Box>
      )}

      <Table
        sx={(theme) => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: 'var(--color-surface)',
        })}
      >
        <TableHead>
          <TableRow>
            {selectableRows && (
              <StyledHeaderCheckboxCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allVisibleRowsSelected}
                  indeterminate={!allVisibleRowsSelected && someVisibleRowsSelected}
                  onChange={(e) => onToggleAllRows?.(e.target.checked, visibleRowIds, rows)}
                  onClick={(e) => e.stopPropagation()}
                  sx={checkboxSx}
                />
              </StyledHeaderCheckboxCell>
            )}
            {columns.map((column) => (
              <HeaderCell
                key={`header-cell-${column.title}`}
                title={column.title}
                fieldName={column.field}
                filterOptions={column.filter ? filterOptions[column.field] : null}
                selectedValues={
                  column.filter && currentFilter[column.field]
                    ? currentFilter[column.field]
                    : []
                }
                onFilterChange={onFilterChange}
                isDate={!!column.dateFilter || (column.field === 'date' && !!column.filter)}
                dateFilterTitle={column.dateFilterTitle}
                dateFilterFromLabel={column.dateFilterFromLabel}
                dateFilterToLabel={column.dateFilterToLabel}
                accessField={column.field === 'access'}
                order={order}
                orderBy={orderBy}
                onSort={handleSort}
                sort={!!column.sort}
              />
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <StyledTableRow
                key={row.id}
                hover
                onClick={() => onRowClick?.(row)}
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
              >
                {selectableRows && (
                  <StyledTableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selectedRowIds.includes(row?.[rowIdField])}
                      onChange={() => onToggleRow?.(row?.[rowIdField], row)}
                      sx={checkboxSx}
                    />
                  </StyledTableCell>
                )}
                {columns.map((column) => (
                  <StyledTableCell key={`cell-${column.title}`} component="th" scope="row" sx={{ width: column?.width || 'auto' }}>
                    {renderCell(column, row)}
                  </StyledTableCell>
                ))}
              </StyledTableRow>
            ))
          ) : (
            <TableRow>
              <StyledTableCell
                colSpan={columns.length + (selectableRows ? 1 : 0)}
                align="center"
                sx={(theme) => ({ py: 6, color: theme.palette.text.secondary })}
              >
                Empty list
              </StyledTableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {!withoutPagination && (
        <TablePagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

TableComponent.propTypes = {
  columns: PropTypes.array,
  rows: PropTypes.array,
  onRowClick: PropTypes.func,
  page: PropTypes.number,
  setPage: PropTypes.func,
  rowsPerPage: PropTypes.number,
  setRowsPerPage: PropTypes.func,
  totalRows: PropTypes.number,
  withoutPagination: PropTypes.bool,
  loading: PropTypes.bool,
  selectableRows: PropTypes.bool,
  selectedRowIds: PropTypes.array,
  onToggleRow: PropTypes.func,
  onToggleAllRows: PropTypes.func,
  rowIdField: PropTypes.string,
};

export default memo(TableComponent);
